/**
 * The time of day a cron fires at, for a field that lets someone change it (#1840). Only a cron whose minute and
 * hour are each one number has one: `0 9 * * 1-5` fires at 09:00, while a cron that fires every 15 minutes has none.
 * The days stay as they are; changing the time rewrites the minute and hour alone.
 */

/** The fields of a five-field cron, or null for anything else. */
function fields(cron: string): string[] | null {
    const parts = cron.trim().split(/\s+/)
    return parts.length === 5 ? parts : null
}

/** A cron field that is one whole number within `max`, as that number, or null. */
function single(field: string | undefined, max: number): number | null {
    if (field === undefined || !/^\d{1,2}$/.test(field)) return null
    const value = Number(field)
    return value <= max ? value : null
}

/** `HH:MM` for a cron that fires at one time of day, or null when its minute or hour is not a single value. */
export function cronTime(cron: string): string | null {
    const parts = fields(cron)
    const minute = single(parts?.[0], 59)
    const hour = single(parts?.[1], 23)
    if (minute === null || hour === null) return null
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/**
 * `cron` firing at `time` (`HH:MM`, as a time field gives it) instead, with its days unchanged: `0 9 * * 1-5` at
 * 07:30 is `30 7 * * 1-5`. Null when the cron has no single time to change or `time` is not a time of day.
 */
export function withCronTime(cron: string, time: string): string | null {
    const parts = fields(cron)
    const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim())
    if (!parts || cronTime(cron) === null || !match) return null
    const hour = Number(match[1])
    const minute = Number(match[2])
    if (hour > 23 || minute > 59) return null
    return [String(minute), String(hour), ...parts.slice(2)].join(' ')
}
